import { format } from 'date-fns'
import { currencyFormat } from 'utils/common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export interface PayoutHistoryItem {
	id: string
	amount: number
	requestedDate: string
	status: 'pending' | 'paid' | 'failed'
	paidDate?: string
	transactionId?: string
	notes?: string
}

export interface PayoutHistoryLogProps {
	payouts: PayoutHistoryItem[]
	className?: string
	currencySymbol?: string
}

export function PayoutHistoryLog({ payouts, className = '', currencySymbol }: PayoutHistoryLogProps) {
	const getStatusConfig = (status: string) => {
		switch (status) {
			case 'paid':
				return {
					color: 'text-green-400 bg-green-900/20 border-green-500/30',
					icon: 'check-circle',
					label: 'Paid',
				}
			case 'pending':
				return {
					color: 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30',
					icon: 'clock',
					label: 'Pending',
				}
			case 'failed':
				return {
					color: 'text-red-400 bg-red-900/20 border-red-500/30',
					icon: 'times-circle',
					label: 'Failed',
				}
			default:
				return {
					color: 'text-gray-400 bg-gray-900/20 border-gray-500/30',
					icon: 'question-circle',
					label: 'Unknown',
				}
		}
	}

	// Sort payouts by requested date (newest first)
	const sortedPayouts = [...payouts].sort(
		(a, b) => new Date(b.requestedDate).getTime() - new Date(a.requestedDate).getTime()
	)

	if (payouts.length === 0) {
		return (
			<div
				className={`bg-primary-surface/[0.4] backdrop-blur-lg rounded-2xl p-6 border border-translucent-plus-border ${className}`}
			>
				<div className="text-center py-8">
					<div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
						<FontAwesomeIcon icon={['fas', 'money-bill-wave']} className="text-gray-400 text-2xl" />
					</div>
					<h4 className="text-lg font-medium text-white mb-2">No Payout History</h4>
					<p className="text-secondary-text text-sm">
						Your payout requests will appear here once you start requesting payouts.
					</p>
				</div>
			</div>
		)
	}

	// Calculate summary statistics
	const totalRequested = payouts.reduce((sum, payout) => sum + payout.amount, 0)
	const totalPaid = payouts.filter(payout => payout.status === 'paid').reduce((sum, payout) => sum + payout.amount, 0)
	const totalPending = payouts
		.filter(payout => payout.status === 'pending')
		.reduce((sum, payout) => sum + payout.amount, 0)
	const totalFailed = payouts
		.filter(payout => payout.status === 'failed')
		.reduce((sum, payout) => sum + payout.amount, 0)

	return (
		<div
			className={`bg-primary-surface/[0.4] backdrop-blur-lg rounded-2xl p-6 border border-translucent-plus-border ${className}`}
		>
			{/* Header - Responsive */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-2 sm:space-y-0">
				<div className="flex items-center space-x-3">
					<div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
						<FontAwesomeIcon icon={['fas', 'history']} className="text-white text-sm" />
					</div>
					<div className="min-w-0">
						<p className="text-sm text-primary-text">
							{payouts.length} {payouts.length === 1 ? 'request' : 'requests'}
						</p>
					</div>
				</div>
			</div>

			{/* Summary Cards - Responsive Grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
				<div className="bg-white/10 rounded-lg p-3 text-center">
					<div className="text-xs text-primary-text mb-1">Total Requested</div>
					<div className="text-sm font-semibold text-white">
						{currencyFormat(totalRequested, currencySymbol)}
					</div>
				</div>
				<div className="hidden">
					<div className="bg-green-900/20 rounded-lg p-3 text-center border border-green-500/30">
						<div className="text-xs text-green-300 mb-1">Paid</div>
						<div className="text-sm font-semibold text-green-200">
							{currencyFormat(totalPaid, currencySymbol)}
						</div>
					</div>
					<div className="bg-yellow-900/20 rounded-lg p-3 text-center border border-yellow-500/30">
						<div className="text-xs text-yellow-300 mb-1">Pending</div>
						<div className="text-sm font-semibold text-yellow-200">
							{currencyFormat(totalPending, currencySymbol)}
						</div>
					</div>
					<div className="bg-red-900/20 rounded-lg p-3 text-center border border-red-500/30">
						<div className="text-xs text-red-300 mb-1">Failed</div>
						<div className="text-sm font-semibold text-red-200">
							{currencyFormat(totalFailed, currencySymbol)}
						</div>
					</div>
				</div>
			</div>

			{/* Payout List */}
			<div className="space-y-3">
				{sortedPayouts.map(payout => {
					const statusConfig = getStatusConfig(payout.status)
					return (
						<div
							key={payout.id}
							className="bg-white/10 rounded-lg p-3 sm:p-4 border border-white/20 hover:border-white/30 transition-all duration-300"
						>
							{/* Header - Responsive Layout */}
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-2 sm:space-y-0">
								<div className="flex items-center space-x-2 sm:space-x-3">
									<div className="text-base sm:text-lg font-bold text-white">
										{currencyFormat(payout.amount, currencySymbol)}
									</div>
									<div className="text-xs text-white/50 font-mono">{payout.id.slice(0, 8)}...</div>
								</div>
								<div
									className={`px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.color} self-start sm:self-auto hidden`}
								>
									<FontAwesomeIcon icon={['fas', statusConfig.icon as any]} className="mr-1" />
									{statusConfig.label}
								</div>
							</div>

							{/* Date Information - Responsive Grid */}
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-sm">
								<div className="flex items-center space-x-2">
									<FontAwesomeIcon
										icon={['fas', 'calendar-plus']}
										className="text-white/50 text-xs flex-shrink-0"
									/>
									<span className="text-primary-text flex-shrink-0 text-xs">Requested:</span>
									<span className="text-white font-medium text-xs">
										{format(new Date(payout.requestedDate), 'MMM dd, yyyy HH:mm')}
									</span>
								</div>
								{payout.paidDate && (
									<div className="flex items-center space-x-2">
										<FontAwesomeIcon
											icon={['fas', 'check-circle']}
											className="text-green-400 text-xs flex-shrink-0"
										/>
										<span className="text-primary-text flex-shrink-0 text-xs">Paid:</span>
										<span className="text-white font-medium text-xs">
											{format(new Date(payout.paidDate), 'MMM dd, yyyy HH:mm')}
										</span>
									</div>
								)}
							</div>

							{/* Notes - Responsive */}
							{payout.notes && (
								<div className="mt-3 p-2 bg-white/10 rounded border border-white/10">
									<span className="text-xs text-primary-text">Notes:</span>
									<p className="text-xs text-white mt-1 break-words">{payout.notes}</p>
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}
